<template>
  <teleport to="#modals">
    <WdsModal
      :show
      :title="$t('Milestones details')"
      custom-size="min(90vw, 900px)"
      @cancel="close"
    >
      <div v-if="loading" class="tw-flex tw-mt-6 tw-justify-center">
        <WdsSpinner :size="40" indeterminate />
      </div>
      <div v-else>
        <WdsAlert
          v-if="(originalMilestones.length ?? 0) > 0 && state === MilestoneEditState.editing"
          info
          icon
          class="tw-mb-4"
        >
          {{ $t('Editing milestones will generate a new contract that the worker must sign.') }}
        </WdsAlert>
        <WdsAlert v-if="validationErrors.length" error icon class="tw-mb-4">
          <ul class="tw-mb-0">
            <li v-for="(error, index) in validationErrors" :key="index">
              {{ error }}
            </li>
          </ul>
        </WdsAlert>
        <MilestonesBlock
          v-model="milestones"
          :milestone-block-state="state"
          :currency
          :original-rate
        />
      </div>
      <template #footer>
        <WdsStack justify-space-between>
          <WdsButton
            v-if="state === MilestoneEditState.display"
            data-test="close-milestone-modal"
            @click.prevent="show = false"
          >
            {{ $t('Close') }}
          </WdsButton>
          <WdsButton v-else data-test="cancel-milestone-changes" @click.prevent="cancel">
            {{ $t('Cancel') }}
          </WdsButton>
          <WdsButton
            v-if="canRequestChanges && state === MilestoneEditState.display && !props.viewOnly"
            primary
            :disabled="loading"
            data-test="request-milestone-changes"
            @click="state = MilestoneEditState.editing"
          >
            {{ $t('Edit') }}
          </WdsButton>
          <WdsStack
            v-if="canRequestChanges && state === MilestoneEditState.editing && !props.viewOnly"
            gap-sm
          >
            <WdsButton
              primary
              :disabled="numOfChanges === 0 || loading"
              data-test="send-milestone-changes"
              @click="saveChanges"
            >
              <WdsSpinner v-if="loading" indeterminate />
              <div v-else>
                {{ $t('Save changes') }}
              </div>
            </WdsButton>
          </WdsStack>
        </WdsStack>
      </template>
    </WdsModal>
    <WdsModal
      :title="$t('Warning')"
      custom-size="min(90vw, 350px)"
      :show="showWarning"
      no-nlose-icon
    >
      <WdsStack direction-column justify-center align-center gap-lg>
        <WdsText large>
          {{
            $t(
              'Closing the modal will result in unsaved changes being lost. Are you sure you want to close the modal?',
            )
          }}
        </WdsText>
        <WdsStack justify-space-between class="tw-w-full">
          <WdsButton ghost @click="showWarning = false">
            {{ $t('Cancel') }}
          </WdsButton>
          <WdsButton error @click="confirmDiscard">
            {{ $t('Discard Changes') }}
          </WdsButton>
        </WdsStack>
      </WdsStack>
    </WdsModal>
  </teleport>
  <slot :open></slot>
</template>

<script setup lang="ts">
import {
  WdsAlert,
  WdsButton,
  WdsModal,
  WdsSpinner,
  WdsStack,
  WdsText,
} from '@worksome/wds-components'
import { computed, ComputedRef, Ref, ref, watch } from 'vue'
import hashSum from 'hash-sum'
import { useMutation, useQuery } from '@vue/apollo-composable'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { MilestoneEditState } from '@/components/Milestones/types'
import MilestonesBlock from '@/components/Milestones/MilestonesBlock.vue'
import { deepClone, isUUID } from '@/utils/tools'
import { MilestonesQuery } from '@/components/Milestones/queries'
import { useFragment } from '@/gql'
import { MilestoneFragment } from '@/components/Milestones/fragments'
import { MilestoneTypeFragment } from '@/gql/graphql'
import {
  CreateMilestonesMutation,
  DeleteMilestonesMutation,
  UpdateMilestonesMutation,
} from '@/components/Milestones/mutations'

const props = defineProps({
  viewOnly: {
    type: Boolean,
    default: false,
  },
  hireId: {
    type: String,
    required: true,
  },
})

const state = ref(MilestoneEditState.display)
const show = ref(false)

const canRequestChanges = true

const {
  loading: milestonesLoading,
  result: milestonesResult,
  error: milestonesError,
  refetch,
} = useQuery(
  MilestonesQuery,
  () => ({
    accountId: currentAccount.globalId,
    hireId: props.hireId,
  }),
  {
    clientId: 'publicApi',
    fetchPolicy: 'no-cache', // external changes can change the milestone state, so we don't cache
  },
)

const currency = computed(() => {
  if (milestonesLoading.value || milestonesError.value) {
    return currentAccount.currency
  }
  return milestonesResult.value?.hire?.currency ?? currentAccount.currency
})

const originalRate = computed(() =>
  milestonesLoading.value
    ? undefined
    : milestonesResult.value?.milestones.data
        .map((milestone) => useFragment(MilestoneFragment, milestone))
        .reduce(
          (count: string, milestone) =>
            (parseFloat(count) + milestone.details.amount || 0).toFixed(2),
          '0',
        ),
)

const { loading: updateMilestonesLoading, mutate: mutateUpdateMilestones } = useMutation(
  UpdateMilestonesMutation,
  {
    clientId: 'publicApi',
  },
)

const { loading: createMilestonesLoading, mutate: mutateCreateMilestones } = useMutation(
  CreateMilestonesMutation,
  {
    clientId: 'publicApi',
  },
)

const { loading: deleteMilestonesLoading, mutate: mutateDeleteMilestones } = useMutation(
  DeleteMilestonesMutation,
  {
    clientId: 'publicApi',
  },
)

const loading = computed(
  () =>
    milestonesLoading.value ||
    deleteMilestonesLoading.value ||
    updateMilestonesLoading.value ||
    createMilestonesLoading.value,
)

const milestones: Ref<MilestoneTypeFragment[]> = ref([])

const validationErrors = ref<string[]>([])
const originalMilestones: ComputedRef<MilestoneTypeFragment[]> = computed(() =>
  milestonesLoading.value
    ? []
    : milestonesResult.value?.milestones.data?.map((milestone) =>
        useFragment(MilestoneFragment, milestone),
      ) ?? [],
)
const originalMilestonesHash: ComputedRef<string[]> = computed(() =>
  originalMilestones.value.map(hashSum),
)

const open = () => {
  refetch()
  show.value = true
}

const updateMilestones = async (milestonesToUpdate: MilestoneTypeFragment[]) =>
  mutateUpdateMilestones({
    input: {
      milestones: milestonesToUpdate.map((milestone: MilestoneTypeFragment) => {
        const { details } = milestone

        return {
          id: milestone.id,
          name: milestone.name,
          purchaseOrderNumber: milestone.purchaseOrderNumber,
          dueDate: details.dueDate,
          amount: details.amount,
        }
      }),
    },
  })

const createMilestones = async (milestonesToCreate: MilestoneTypeFragment[]) =>
  mutateCreateMilestones({
    input: {
      milestones: milestonesToCreate.map((milestone: MilestoneTypeFragment) => {
        const { details } = milestone

        return {
          name: milestone.name,
          purchaseOrderNumber: milestone.purchaseOrderNumber,
          dueDate: details.dueDate,
          amount: details.amount,
          hire: props.hireId,
        }
      }),
    },
  })

const deleteMilestones = async (milestonesToDelete: MilestoneTypeFragment[]) =>
  mutateDeleteMilestones({
    input: {
      milestones: milestonesToDelete.map((milestone) => ({
        id: milestone.id,
      })),
    },
  })

const undoChanges = () => {
  milestones.value = [...originalMilestones.value.map(deepClone)]
}

const saveChanges = async () => {
  try {
    const milestonesToUpdate = milestones.value.filter(
      (milestone) =>
        !originalMilestonesHash.value.includes(hashSum(milestone)) && !isUUID(milestone.id),
    )

    const milestonesToCreate = milestones.value.filter((milestone) => isUUID(milestone.id))

    const milestonesToDelete = originalMilestones.value.filter(
      ({ id }) => !milestones.value.some((milestone) => milestone.id === id),
    )

    const requests: Promise<any>[] = []

    if (milestonesToCreate.length > 0) {
      requests.push(createMilestones(milestonesToCreate))
    }
    if (milestonesToUpdate.length > 0) {
      requests.push(updateMilestones(milestonesToUpdate))
    }
    if (milestonesToDelete.length > 0) {
      requests.push(deleteMilestones(milestonesToDelete))
    }

    await Promise.all(requests)
    await refetch()

    state.value = MilestoneEditState.display

    validationErrors.value = []
  } catch (error: any) {
    validationErrors.value = []

    if (error?.graphQLErrors) {
      const gqlErrors = error.graphQLErrors[0]?.extensions?.validation || {}
      validationErrors.value = Object.values(gqlErrors)
        .flat()
        .map((validationError) => String(validationError))
    } else {
      validationErrors.value.push('An unexpected error occurred. Please try again.')
    }
  }
}

const cancel = () => {
  state.value = MilestoneEditState.display
  undoChanges()
}

const numOfChanges = computed(() => {
  // First part counts the changed milestones as well as the new ones
  const newAndChanged =
    milestones.value.map((m) => hashSum(m)).filter((h) => !originalMilestonesHash.value.includes(h))
      .length ?? 0

  const deletedChange = originalMilestones.value
    .map(({ id }) => id)
    .filter((id_) => !milestones.value.map(({ id }) => id).includes(id_)).length // Counts the deleted number of milestones
  return newAndChanged + deletedChange
})

const showWarning = ref(false)

const confirmDiscard = () => {
  undoChanges()
  show.value = false
  showWarning.value = false
  state.value = MilestoneEditState.display
}

const close = () => {
  // Fire warning here if we have changes.
  if (numOfChanges.value === 0) {
    confirmDiscard()
  } else {
    showWarning.value = true
  }
}

watch(originalMilestones, undoChanges) // Keeps milestones upto date with the gql version when needed
</script>
